import { FC, useEffect, useState } from 'react';

import { VIEW_DATE_FORMAT } from 'utils/constants/date';
import { formatDate } from 'utils/date';
import { setShownRequisitesWarnings } from 'utils/requisites';

import Button from 'bloko/blocks/button';
import Column from 'bloko/blocks/column';
import Information, { InformationKind } from 'bloko/blocks/information';
import Link from 'bloko/blocks/link';
import Modal, { ModalContent, ModalFooter, ModalHeader, ModalTitle } from 'bloko/blocks/modal';
import Text, { TextImportance } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import { formatToReactComponent } from 'bloko/common/trl';

import useTrls from 'hooks/translation/useTrls';

import { checkRequisites } from 'components/RequisitesWarningModal/checkRequisites';

import { useDispatch, useSelector } from 'store/utils';

import 'components/RequisitesWarningModal/RequisitesWarningModal.less';

const ORD_LINK = 'https://rkn.gov.ru/register-ord';

const RequisitesWarningModal: FC = () => {
    const phones = useSelector((state) => state.clickme.phones);
    const user = useSelector((state) => state.user.user);
    const warning = useSelector((state) => state.requisites.requisitesWarning);

    const dispatch = useDispatch();

    const [isVisible, setVisible] = useState(false);

    const trl = useTrls();

    useEffect(() => {
        if (user) {
            void dispatch(checkRequisites(() => setVisible(true)));
        }
    }, [dispatch, user]);

    if (!warning) {
        return null;
    }

    const { payerId, payerName, invoiceNumber, paymentDate } = warning;

    const onClose = () => {
        setVisible(false);
        setShownRequisitesWarnings(payerId);
    };

    return (
        <Modal visible={isVisible} onClose={onClose}>
            <ModalHeader>
                <ModalTitle>{trl('requisites.warning.title')}</ModalTitle>
            </ModalHeader>
            <ModalContent>
                <Column xs="4" s="7" m="7" l="7" container>
                    <Text>{trl('requisites.warning.description')}</Text>
                    <VSpacing base={3} />
                    <div className="requisites-warning-content">
                        <div>
                            <Text importance={TextImportance.Secondary}>
                                {trl('requisites.warning.new.payer.title')}
                            </Text>
                            <VSpacing base={1} />
                            <Text>{payerName}</Text>
                        </div>
                        {invoiceNumber && paymentDate && (
                            <div>
                                <Text importance={TextImportance.Secondary}>
                                    {trl('requisites.warning.new.invoice.title')}
                                </Text>
                                <VSpacing base={1} />
                                <Text>
                                    {trl('requisites.warning.new.invoice', [
                                        invoiceNumber,
                                        formatDate(paymentDate, VIEW_DATE_FORMAT),
                                    ])}
                                </Text>
                            </div>
                        )}
                    </div>
                    <VSpacing base={5} />
                    <Information kind={InformationKind.Warning}>
                        <Text strong>{trl('requisites.warning.information.title')}</Text>
                        <VSpacing base={2} />
                        <Text>
                            {formatToReactComponent(trl('requisites.warning.information.description.part1'), {
                                '[0]': (
                                    <Link target="_blank" rel="noopener noreferrer" href={ORD_LINK} disableVisited>
                                        {trl('requisites.warning.information.link')}
                                    </Link>
                                ),
                            })}
                        </Text>
                        <VSpacing base={2} />
                        <Text>{trl('requisites.warning.information.description.part2')}</Text>
                        <VSpacing base={3} />
                        <Text Element="span" strong>
                            {trl('requisites.warning.information.tel')}
                        </Text>{' '}
                        <Text Element="span">{phones?.other?.tel}</Text>{' '}
                        <Text Element="span">{trl('requisites.warning.information.tel.free')}</Text>
                    </Information>
                </Column>
            </ModalContent>
            <ModalFooter>
                <Button onClick={onClose}>{trl('requisites.warning.close')}</Button>
            </ModalFooter>
        </Modal>
    );
};

export default RequisitesWarningModal;
